<script setup lang="ts">
import { onBeforeMount, reactive } from 'vue';
import { useRosterStore } from '@/stores/rosters';
import { getTranslations } from '@/lib/util/i18n';
import { displayFromISO, displayFromISOWithWeekday } from '@/lib/util/dateTime';
import { useUserStore } from '@/stores/user';
import { useAreaStore } from '@/stores/areas';
import { useTimesheetStore } from '@/stores/timesheets';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { Rosters } from '@/lib/request/types/roster';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'upcoming-shifts-card',
});

const areas = useAreaStore();
const timesheets = useTimesheetStore();
const timesheetSetup = useTimesheetSetupStore();
const rosters = useRosterStore();
const user = useUserStore();

const state = reactive({
  nextRosters: [] as Rosters,
});

function areDatesOnSameDay(roster: string, nextRoster: string) {
  return (
    displayFromISOWithWeekday(roster) == displayFromISOWithWeekday(nextRoster)
  );
}

onBeforeMount(async () => {
  await rosters.loadForActiveUser();

  state.nextRosters = rosters.getNextRostersByEmployeeId(
    user.activeUserOrThrow.Id
  );

  if (!timesheets.isOnShift) {
    state.nextRosters.shift();
  }
});
</script>

<template>
  <div
    v-if="
      state.nextRosters.length &&
      !timesheets.isLoading &&
      !rosters.isLoading &&
      !timesheetSetup.isLoading
    "
    class="upcoming-shifts-card"
  >
    <h3 class="__title">
      {{ state.nextRosters.length != 1 ? t('title-plural') : t('title') }}
    </h3>
    <div
      v-for="(roster, index) in state.nextRosters"
      :key="roster.Id"
      class="__upcoming-shift-item"
    >
      <div
        class="__upcoming-shift-item-date"
        v-if="
          index == 0 ||
          !areDatesOnSameDay(
            roster.StartTimeLocalized,
            state.nextRosters[index - 1].StartTimeLocalized
          )
        "
      >
        {{ displayFromISOWithWeekday(roster.StartTimeLocalized) }}
      </div>
      <span class="__upcoming-shift-item-details">{{
        t('upcoming-shift-item', {
          startTime: displayFromISO(roster.StartTimeLocalized),
          endTime: displayFromISO(roster.EndTimeLocalized),
          area: areas.getAreaById(roster.OperationalUnit)?.OperationalUnitName,
        })
      }}</span>
      <span v-if="roster.Comment" class="__comment">{{ roster.Comment }}</span>
    </div>
  </div>
</template>

<style scoped>
.upcoming-shifts-card {
  max-width: 808px;
  border: 1px solid #d8d7dc;
  border-radius: 32px;
  padding: 24px;
  margin: 24px auto;
}

.__title {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 12px;
}

@media (max-width: 900px) {
  .upcoming-shifts-card {
    margin: 1rem 2rem;
  }
}

.__upcoming-shift-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.__upcoming-shift-item-date {
  font-size: 17px;
  font-weight: 600;
  line-height: 18px;
  margin: 10px 0 20px;
}
.__upcoming-shift-item-details {
  font-size: 17px;
}

.__comment {
  font-size: 15px;
  color: var(--co-text-secondary);
  margin-top: 8px;
}
</style>
